/**
 * ConnectionGovernor — caps the number of concurrent per-message SSE streams.
 *
 * Browsers enforce a per-origin HTTP/1.1 connection limit (typically 6).
 * We reserve 2 for the list-events stream and other requests, so per-message
 * streams are capped at MAX_CONCURRENT (4).  Additional requests are queued
 * and dispatched as active streams complete.
 */

const MAX_CONCURRENT = 4;

export type StreamFactory = (eventId: string, abortSignal: AbortSignal) => void;

interface QueueEntry {
  eventId: string;
  factory: StreamFactory;
  controller: AbortController;
}

export class ConnectionGovernor {
  private active: Map<string, AbortController> = new Map();
  private queue: QueueEntry[] = [];

  /**
   * Request a per-message stream for `eventId`.
   * If a stream for that id is already active, this is a no-op.
   * Returns an AbortController the caller can use to cancel early.
   */
  request(eventId: string, factory: StreamFactory): AbortController {
    if (this.active.has(eventId)) {
      // Return existing controller so the caller can still cancel it
      return this.active.get(eventId)!;
    }

    const controller = new AbortController();

    if (this.active.size < MAX_CONCURRENT) {
      this._open(eventId, factory, controller);
    } else {
      this.queue.push({ eventId, factory, controller });
    }

    return controller;
  }

  /**
   * Signal that a stream has finished (successfully or with error).
   * Dispatches the next queued entry if one exists.
   */
  release(eventId: string): void {
    this.active.delete(eventId);
    this._flush();
  }

  /** Cancel all active streams and clear the queue. */
  cancelAll(): void {
    this.active.forEach(ctrl => ctrl.abort());
    this.active.clear();
    this.queue.forEach(e => e.controller.abort());
    this.queue = [];
  }

  /** Number of active streams (for testing). */
  get activeCount(): number {
    return this.active.size;
  }

  private _open(eventId: string, factory: StreamFactory, controller: AbortController): void {
    this.active.set(eventId, controller);
    factory(eventId, controller.signal);
  }

  private _flush(): void {
    while (this.queue.length > 0 && this.active.size < MAX_CONCURRENT) {
      const next = this.queue.shift()!;
      if (!next.controller.signal.aborted) {
        this._open(next.eventId, next.factory, next.controller);
      }
    }
  }
}
